import { Colors, Icon } from '@amelio/ui-library';

import { AddSkillsModal } from './components/AddSkillsModal';
import { CompanyValuesStep } from './components/CompanyValuesStep';
import { SkillReviewer } from './components/CompetencySelector';
import { NoCompetenciesNotice } from './components/NoCompetenciesNotice';
import { SkillChooser } from './components/SkillChooser';
import { StartAssessmentStep } from './components/StartAssessmentStep';
import { StepBadge } from './components/StepBadge';
import { TemplateSelector } from './components/TemplateSelector';
import { useManagerAssessmentSetup } from './hooks/useManagerAssessmentSetup';
import { SETUP_STEP_TITLE } from './setupStyles';

import { useGetAssessmentTemplates } from '@/api/cycle-evaluation-services/manager-assessment';
import { useAmelioReviewTemplates } from '@/api/review-services/review-template';
import { EmployeeSelector } from '@/components/EmployeeSelector/EmployeeSelector';
import { HeaderArrowIllustration } from '@/components/Illustrations/Illustration';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useAutoScroll } from '@/hooks/useAutoScroll';

export const ManagerAssessmentSetupPage = () => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const {
        competencies,
        isLoadingCompetencies,
        selectedEmployeeId,
        preselectedEmployeeId,
        selectedSkillOption,
        selectedMethod,
        selectedTemplateId,
        selectedSkills,
        includeCompanyValues,
        skillCounts,
        isSection2Unlocked,
        hasNoCompetencies,
        isSection3TemplateUnlocked,
        isSection3ReviewUnlocked,
        isTemplateFlowComplete,
        isSection4Unlocked,
        setSelectedTemplateId,
        setIncludeCompanyValues,
        handleEmployeeSelect,
        handleDeselectEmployee,
        handleSkillOptionSelect,
        handleMethodSelect,
        handleRemoveSkill,
        isAddSkillsOpen,
        canStartAssessment,
        handleOpenAddSkills,
        handleCloseAddSkills,
        handleConfirmAddSkills,
        handleStartAssessment,
    } = useManagerAssessmentSetup();

    const { data: templates = [] } = useGetAssessmentTemplates(isSection3TemplateUnlocked);

    const { data: amelioTemplates = [] } = useAmelioReviewTemplates(
        EvaluationType.MANAGER_EVALUATION,
        { enabled: isSection3TemplateUnlocked },
    );

    const section2Ref = useAutoScroll<HTMLDivElement>(isSection2Unlocked, [isSection2Unlocked]);
    const section3TemplateRef = useAutoScroll<HTMLDivElement>(isSection3TemplateUnlocked, [
        isSection3TemplateUnlocked,
    ]);
    const section3ReviewRef = useAutoScroll<HTMLDivElement>(isSection3ReviewUnlocked, [
        isSection3ReviewUnlocked,
        selectedSkillOption,
        selectedMethod,
    ]);
    const section4Ref = useAutoScroll<HTMLDivElement>(
        isSection4Unlocked || isTemplateFlowComplete,
        [isSection4Unlocked, isTemplateFlowComplete],
    );

    return (
        <>
            <div
                className={'manager-assessment-setup-layout mx-auto px-4 py-4 sm:!px-10 sm:!py-6'}
                data-testid={'manager-assessment-setup-page'}
            >
                {/* Header */}
                <div
                    className={
                        'relative mb-8 flex flex-col items-start gap-4 sm:!mb-16 sm:!flex-row sm:!gap-6 md:!mb-32'
                    }
                >
                    <div
                        className={
                            'flex w-full flex-col items-center gap-4 rounded-2xl border border-white bg-white/45 p-4 backdrop-blur-[2px] sm:!max-w-[581px] sm:!flex-row sm:!gap-6 sm:!p-6'
                        }
                    >
                        <div
                            className={
                                'bg-primary-50 flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full sm:!h-[60px] sm:!w-[60px]'
                            }
                        >
                            {/* The heading beside it already names the page; the icon repeats it. */}
                            <Icon
                                name={'users-profile-2user'}
                                size={32}
                                color={Colors.PRIMARY_500}
                                ariaHidden
                            />
                        </div>

                        <div className={'min-w-0 flex-1 text-center sm:!text-left'}>
                            <h2 className={`mb-2 sm:!mb-3 ${SETUP_STEP_TITLE}`}>
                                {t('managerAssessmentSetup.title')}
                            </h2>

                            <p className={'text-xs leading-normal text-gray-900 sm:!text-[11px]'}>
                                {t('managerAssessmentSetup.description', {
                                    managerLabel: getManagerLabel(),
                                })}
                            </p>
                        </div>
                    </div>

                    {/* Decorative Arrow - hidden on mobile */}
                    <div
                        className={'pointer-events-none absolute hidden lg:!block'}
                        style={{ left: '535px', top: '53px' }}
                    >
                        <HeaderArrowIllustration className={'h-[251px] w-[258px]'} />
                    </div>
                </div>

                {/* Main Content Container */}
                <div
                    className={
                        'rounded-2xl border border-gray-200 bg-white/60 px-4 py-6 sm:!px-6 sm:!px-[60px] sm:!py-8 md:!mt-[128px] lg:!px-[90px] lg:!py-[67px] lg:!py-[97px]'
                    }
                >
                    <div
                        className={
                            'mx-auto flex max-w-[1172px] flex-col gap-6 sm:!gap-10 md:!gap-[72px]'
                        }
                    >
                        {/* Step 1: Select Employee */}
                        <div className={'flex flex-col gap-6 sm:!gap-10'}>
                            <div className={'flex items-center gap-2 sm:!gap-3'}>
                                <StepBadge
                                    step={1}
                                    className={
                                        '!h-[24px] !w-[24px] text-sm font-semibold sm:!h-[30px] sm:!w-[30px] sm:!text-[18px]'
                                    }
                                />

                                <h3 className={SETUP_STEP_TITLE}>
                                    {t('managerAssessmentSetup.selectEmployee')}
                                </h3>
                            </div>

                            <EmployeeSelector
                                selectedEmployeeId={selectedEmployeeId}
                                preselectedEmployeeId={preselectedEmployeeId}
                                onSelectEmployee={handleEmployeeSelect}
                                onDeselectEmployee={handleDeselectEmployee}
                            />

                            <div className={'bg-muted mt-6 h-px w-full sm:!mt-10'} />
                        </div>

                        {/* Step 2: Choose Skills to Assess */}
                        {isSection2Unlocked && (
                            <div
                                ref={section2Ref}
                                className={'mt-6 flex flex-col gap-4 sm:!mt-[40px] sm:!gap-6'}
                                data-testid={'section-choose-skills'}
                            >
                                <div className={'flex items-start gap-2 sm:!gap-3'}>
                                    <StepBadge
                                        step={2}
                                        className={
                                            '!h-[24px] !w-[24px] text-sm font-semibold sm:!h-[30px] sm:!w-[30px] sm:!text-[18px]'
                                        }
                                    />

                                    <h3 className={SETUP_STEP_TITLE}>
                                        {t('managerAssessmentSetup.chooseSkills')}
                                    </h3>
                                </div>

                                {hasNoCompetencies && selectedEmployeeId ? (
                                    <NoCompetenciesNotice employeeId={selectedEmployeeId} />
                                ) : (
                                    <SkillChooser
                                        skillCounts={skillCounts}
                                        selectedSkillOption={selectedSkillOption}
                                        selectedMethod={selectedMethod}
                                        onSkillOptionSelect={handleSkillOptionSelect}
                                        onMethodSelect={handleMethodSelect}
                                    />
                                )}

                                <div className={'bg-muted mt-6 h-px w-full sm:!mt-8'} />
                            </div>
                        )}

                        {/* Step 3: Select Template (template flow) */}
                        {isSection3TemplateUnlocked && (
                            <div
                                ref={section3TemplateRef}
                                className={'flex flex-col gap-4 sm:!gap-6'}
                                data-testid={'section-select-template'}
                            >
                                <div className={'flex items-start gap-2 sm:!gap-3'}>
                                    <StepBadge
                                        step={3}
                                        className={
                                            '!h-[24px] !w-[24px] text-sm font-semibold sm:!h-[30px] sm:!w-[30px] sm:!text-[18px]'
                                        }
                                    />

                                    <div className={'flex-1'}>
                                        <h3 className={`mb-1 ${SETUP_STEP_TITLE}`}>
                                            {t('managerAssessmentSetup.selectTemplate')}
                                        </h3>

                                        <p className={'text-xs text-gray-900 sm:!text-sm'}>
                                            {t('managerAssessmentSetup.noSurveyNote')}
                                        </p>
                                    </div>
                                </div>

                                <TemplateSelector
                                    templates={templates}
                                    amelioTemplates={amelioTemplates}
                                    selectedTemplateId={selectedTemplateId}
                                    onSelectTemplate={setSelectedTemplateId}
                                />

                                <div className={'bg-muted mt-4 h-px w-full sm:!mt-6'} />
                            </div>
                        )}

                        {/* Step 3: Review Skills (preset/custom flow) */}
                        {isSection3ReviewUnlocked && (
                            <div
                                ref={section3ReviewRef}
                                className={'flex flex-col gap-4 sm:!gap-6'}
                                data-testid={'section-review-skills'}
                            >
                                <div className={'flex items-start gap-2 sm:!gap-3'}>
                                    <StepBadge
                                        step={3}
                                        className={
                                            '!h-[24px] !w-[24px] text-sm font-semibold sm:!h-[30px] sm:!w-[30px] sm:!text-[18px]'
                                        }
                                    />

                                    <div className={'flex flex-col gap-1 sm:!gap-2'}>
                                        <h3 className={SETUP_STEP_TITLE}>
                                            {t('managerAssessmentSetup.reviewSkills')}
                                        </h3>

                                        <p className={'text-sm font-normal text-gray-900'}>
                                            {`${t('managerAssessmentSetup.noSurveyNote')} ${t('managerAssessmentSetup.estimatedTime')}`}
                                        </p>
                                    </div>
                                </div>

                                <SkillReviewer
                                    selectedSkills={selectedSkills}
                                    employeeCompetencies={competencies}
                                    allCompetencies={competencies}
                                    onRemoveSkill={handleRemoveSkill}
                                    onAddSkills={handleOpenAddSkills}
                                    isLoading={isLoadingCompetencies}
                                />

                                <div className={'bg-muted mt-4 h-px w-full sm:!mt-6'} />

                                <div className={'mt-4 flex items-start gap-2 sm:!mt-6 sm:!gap-3'}>
                                    <StepBadge
                                        step={4}
                                        className={
                                            '!h-[24px] !w-[24px] text-sm font-semibold sm:!h-[30px] sm:!w-[30px] sm:!text-[18px]'
                                        }
                                    />

                                    <h3 className={SETUP_STEP_TITLE}>
                                        {t('managerAssessmentSetup.companyValues.title')}
                                    </h3>
                                </div>

                                <CompanyValuesStep
                                    includeCompanyValues={includeCompanyValues}
                                    onToggle={setIncludeCompanyValues}
                                />

                                <div className={'bg-muted mt-4 h-px w-full sm:!mt-6'} />

                                {isSection4Unlocked && (
                                    <div className={'mt-4 flex flex-col gap-4 sm:!mt-6 sm:!gap-6'}>
                                        <div className={'flex items-start gap-2 sm:!gap-3'}>
                                            <StepBadge
                                                step={5}
                                                className={
                                                    '!h-[24px] !w-[24px] text-sm font-semibold sm:!h-[30px] sm:!w-[30px] sm:!text-[18px]'
                                                }
                                            />

                                            <h3 className={SETUP_STEP_TITLE}>
                                                {t('managerAssessmentSetup.startAssessment')}
                                            </h3>
                                        </div>

                                        <StartAssessmentStep
                                            disabled={!canStartAssessment}
                                            onStart={handleStartAssessment}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Template flow: Steps 4 + 5 */}
                        {isTemplateFlowComplete && (
                            <div
                                ref={section4Ref}
                                className={'flex flex-col gap-4 sm:!gap-6'}
                                data-testid={'section-template-finish'}
                            >
                                <div className={'flex items-start gap-2 sm:!gap-3'}>
                                    <StepBadge
                                        step={4}
                                        className={
                                            '!h-[24px] !w-[24px] text-sm font-semibold sm:!h-[30px] sm:!w-[30px] sm:!text-[18px]'
                                        }
                                    />

                                    <h3 className={SETUP_STEP_TITLE}>
                                        {t('managerAssessmentSetup.companyValues.title')}
                                    </h3>
                                </div>

                                <CompanyValuesStep
                                    includeCompanyValues={includeCompanyValues}
                                    onToggle={setIncludeCompanyValues}
                                />

                                <div className={'bg-muted h-px w-full'} />

                                <div className={'flex items-start gap-2 sm:!gap-3'}>
                                    <StepBadge
                                        step={5}
                                        className={
                                            '!h-[24px] !w-[24px] text-sm font-semibold sm:!h-[30px] sm:!w-[30px] sm:!text-[18px]'
                                        }
                                    />

                                    <h3 className={SETUP_STEP_TITLE}>
                                        {t('managerAssessmentSetup.startAssessment')}
                                    </h3>
                                </div>

                                <StartAssessmentStep
                                    disabled={!canStartAssessment}
                                    onStart={handleStartAssessment}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AddSkillsModal
                isOpen={isAddSkillsOpen}
                onClose={handleCloseAddSkills}
                competencies={competencies}
                selectedSkillIds={selectedSkills.map((s) => s.id)}
                onConfirm={handleConfirmAddSkills}
            />
        </>
    );
};
